import { isImage, isVideo, isDocument, isAudio } from 'utilities/file';

class Pages {

  constructor(basePath, baseUserPath, apiClient) {

    this.apiClient = apiClient;
    this.basePath = `${baseUserPath}/pages`;
    this.galleriesBasePath = `${basePath}/galleries`;
    this.carouselsBasePath = `${basePath}/carousels`;

  }

  async createPageDraft() {

    const path = this.basePath;

    return this.apiClient.post(path, {}, {}, {});

  }

  async bootstrap(query, urlParameters) {

    const path = `${this.basePath}/bootstrap`;

    return this.apiClient.post(path, {}, {}, { query, urlParameters });

  }

  async getAllActivePages(queryOptions) {

    const path = this.basePath;

    return this.apiClient.get(path, {}, queryOptions);

  }

  async getPagesBySearchTerm(searchTerm) {

    const path = `${this.basePath}/search`;

    return this.apiClient.post(path, {}, {}, { searchTerm });

  }

  async updatePageColorsById(pageId, colors) {

    const path = `${this.basePath}/:pageId/colors`;

    return this.apiClient.post(path, { pageId }, {}, colors);

  }

  async updatePageFontFamilyById(pageId, family) {

    const path = `${this.basePath}/:pageId/font/family`;

    return this.apiClient.post(path, { pageId }, {}, { family });

  }

  async updatePageFontSizeById(pageId, size) {

    const path = `${this.basePath}/:pageId/font/size`;

    return this.apiClient.post(path, { pageId }, {}, { size });

  }

  async updatePageContentById(pageId, content, updateKey) {

    const path = `${this.basePath}/:pageId/content`;

    return this.apiClient.put(path, { pageId }, {}, { content, updateKey });

  }

  async createPage(pageId, page) {

    const path = `${this.basePath}/:pageId/create`;

    return this.apiClient.post(path, { pageId }, {}, page);

  }

  async movePage(source, destination) {

    const path = `${this.basePath}/:source/move/:destination`;

    return this.apiClient.post(path, { source, destination }, {}, {});

  }

  async setupPagesManager(pageId, data) {

    const path = `${this.basePath}/:pageId/from-data-collection`;

    return this.apiClient.post(path, { pageId }, {}, data);

  }

  async setupPagesManagerForPage(pageId, data) {

    const path = `${this.basePath}/:pageId/managers`;

    return this.apiClient.post(path, { pageId }, {}, data);

  }

  async createPageFromTemplate(payload) {

    const path = `${this.basePath}/from-template`;

    return this.apiClient.post(path, {}, {}, payload);

  }

  async askAIToGeneratePage(payload) {

    const path = `${this.basePath}/using-ai`;

    return this.apiClient.post(path, {}, {}, payload);

  }

  async validateCreatePageInput(pageId, page) {

    const path = `${this.basePath}/:pageId/create/validate`;

    return this.apiClient.post(path, { pageId }, {}, page);

  }

  async updatePageSettingsById(pageId, settings) {

    const path = `${this.basePath}/:pageId/settings`;

    return this.apiClient.put(path, { pageId }, {}, settings);

  }

  async updatePageSettingsFacade(pageId, data) {

    const path = `${this.basePath}/:pageId/settings-facade`;

    return this.apiClient.put(path, { pageId }, {}, data);

  }

  async uploadSocialMediaImage(pageId, file) {

    const path = `${this.basePath}/:pageId/search-engine-settings-image`;

    const data = new FormData();
    data.append('file', file);

    return this.apiClient.put(path, { pageId }, {}, data, true);

  }

  async validateUpdatePageSettingsByIdInput(pageId, settings) {

    const path = `${this.basePath}/:pageId/settings/validate`;

    return this.apiClient.put(path, { pageId }, {}, settings);

  }

  async getPopulatedPageById(pageId, projection) {

    const path = `${this.basePath}/:pageId/editor`;

    return this.apiClient.get(path, { pageId }, { projection });

  }

  async getPopulatedSignaturePanelByPageId(pageId) {

    const path = `${this.basePath}/:pageId/signature-panel`;

    return this.apiClient.get(path, { pageId }, {});

  }

  async getPopulatedPageForEditorById(pageId, projection) {

    const path = `${this.basePath}/:pageId/editor`;

    return this.apiClient.get(path, { pageId }, { projection });

  }

  async getPageEditorsMapById(pageId) {

    const path = `${this.basePath}/:pageId/editors-map`;

    return this.apiClient.get(path, { pageId }, {});

  }

  async getPaginatedPageVersionsByPageId(pageId, queryOptions) {

    const path = `${this.basePath}/:pageId/versions`;

    return this.apiClient.get(path, { pageId }, queryOptions);

  }

  async getPageVersionById(pageId, pageVersionId) {

    const path = `${this.basePath}/:pageId/versions/:pageVersionId`;

    return this.apiClient.get(path, { pageId, pageVersionId }, {}, {});

  }

  async rollbackPage(pageId, pageVersionId) {

    const path = `${this.basePath}/:pageId/versions/:pageVersionId/rollback`;

    return this.apiClient.post(path, { pageId, pageVersionId }, {}, {});

  }

  async uploadPageFile(pageId, file) {

    let path = `${this.basePath}/:pageId`;

    if (isImage(file)) {
      path = `${path}/images`;
    } else if (isVideo(file)) {
      path = `${path}/videos`;
    } else if (isDocument(file)) {
      path = `${path}/documents`;
    } else if (isAudio(file)) {
      path = `${path}/audios`;
    } else {
      path = `${path}/documents`;
    }

    const data = new FormData();
    data.append('file', file);

    return this.apiClient.put(path, { pageId }, {}, data, true);

  }

  async cloneFile(pageId, fileId) {

    const path = `${this.basePath}/:pageId/files/:fileId/clone`;

    return this.apiClient.post(path, { pageId, fileId }, {}, {});

  }

  async cloneForm(pageId, formId) {

    const path = `${this.basePath}/:pageId/forms/:formId/clone`;

    return this.apiClient.post(path, { pageId, formId }, {}, {});

  }

  async duplicatePageById(pageId) {

    const path = `${this.basePath}/:pageId/duplicate`;

    return this.apiClient.post(path, { pageId }, {}, {});

  }

  async deletePageById(pageId) {

    const path = `${this.basePath}/:pageId`;

    return this.apiClient.delete(path, { pageId }, {});

  }

  async getPagesManagerByPageId(pageId) {

    const path = `${this.basePath}/:pageId/manager`;

    return this.apiClient.get(path, { pageId }, {});

  }

  async getPageDataByPageId(pageId) {

    const path = `${this.basePath}/:pageId/record`;

    return this.apiClient.get(path, { pageId }, {});

  }

  async getRecordsForPageAndDataCollectionNode(pageId, node) {

    const path = `${this.basePath}/:pageId/data-collection-records`;

    return this.apiClient.post(path, { pageId }, {}, node);

  }

  generatePDF(pageId) {

    const path = `${this.basePath}/:pageId/pdf`;

    const url = this.apiClient.getURL(path, { pageId }, {});

    url.searchParams.append('authorization', this.apiClient.authorization);

    return url;

  }

  generateTextReplacements(payload) {

    const path = `${this.basePath}/:pageId/assistant/quick-edit`;

    return this.apiClient.post(path, { pageId: 'sample' }, {}, payload);

  }

  async createGallery(pageId) {

    const path = `${this.galleriesBasePath}`;

    return this.apiClient.post(path, {}, {}, { pageId });

  }

  async getGalleryById(galleryId) {

    const path = `${this.galleriesBasePath}/:galleryId`;

    return this.apiClient.get(path, { galleryId }, {});

  }

  async createGalleryItem(galleryId, payload) {

    const path = `${this.galleriesBasePath}/:galleryId/items`;

    return this.apiClient.post(path, { galleryId }, {}, payload);

  }

  async updateGalleryItemCaption(galleryId, galleryItemId, caption) {

    const path = `${this.galleriesBasePath}/:galleryId/items/:galleryItemId/caption`;

    return this.apiClient.put(path, { galleryId, galleryItemId }, {}, { caption });

  }

  async updateGalleryItemFile(galleryId, galleryItemId, file) {

    const path = `${this.galleriesBasePath}/:galleryId/items/:galleryItemId/file`;

    return this.apiClient.put(path, { galleryId, galleryItemId }, {}, file);

  }

  async markGalleryForDeletion(galleryId) {

    const path = `${this.galleriesBasePath}/:galleryId/deletion`;

    return this.apiClient.post(path, { galleryId }, {}, {});

  }

  async unmarkGalleryForDeletion(galleryId) {

    const path = `${this.galleriesBasePath}/:galleryId/deletion`;

    return this.apiClient.delete(path, { galleryId }, {});

  }

  async markGalleryItemForDeletion(galleryId, galleryItemId) {

    const path = `${this.galleriesBasePath}/:galleryId/items/:galleryItemId/deletion`;

    return this.apiClient.post(path, { galleryId, galleryItemId }, {}, {});

  }

  async unmarkGalleryItemForDeletion(galleryId, galleryItemId) {

    const path = `${this.galleriesBasePath}/:galleryId/items/:galleryItemId/deletion`;

    return this.apiClient.delete(path, { galleryId, galleryItemId }, {});

  }

  async createCarousel(pageId) {

    const path = `${this.carouselsBasePath}`;

    return this.apiClient.post(path, {}, {}, { pageId });

  }

  async getCarouselById(carouselId) {

    const path = `${this.carouselsBasePath}/:carouselId`;

    return this.apiClient.get(path, { carouselId }, {});

  }

  async createCarouselItem(carouselId, payload) {

    const path = `${this.carouselsBasePath}/:carouselId/items`;

    return this.apiClient.post(path, { carouselId }, {}, payload);

  }

  async updateCarouselItemCaption(carouselId, carouselItemId, caption) {

    const path = `${this.carouselsBasePath}/:carouselId/items/:carouselItemId/caption`;

    return this.apiClient.put(path, { carouselId, carouselItemId }, {}, { caption });

  }

  async updateCarouselItemFile(carouselId, carouselItemId, file) {

    const path = `${this.carouselsBasePath}/:carouselId/items/:carouselItemId/file`;

    return this.apiClient.put(path, { carouselId, carouselItemId }, {}, file);

  }

  async markCarouselForDeletion(carouselId) {

    const path = `${this.carouselsBasePath}/:carouselId/deletion`;

    return this.apiClient.post(path, { carouselId }, {}, {});

  }

  async unmarkCarouselForDeletion(carouselId) {

    const path = `${this.carouselsBasePath}/:carouselId/deletion`;

    return this.apiClient.delete(path, { carouselId }, {});

  }

  async markCarouselItemForDeletion(carouselId, carouselItemId) {

    const path = `${this.carouselsBasePath}/:carouselId/items/:carouselItemId/deletion`;

    return this.apiClient.post(path, { carouselId, carouselItemId }, {}, {});

  }

  async unmarkCarouselItemForDeletion(carouselId, carouselItemId) {

    const path = `${this.carouselsBasePath}/:carouselId/items/:carouselItemId/deletion`;

    return this.apiClient.delete(path, { carouselId, carouselItemId }, {});

  }

}

export default Pages;